/**
 * GraphExplainer Component (#27)
 *
 * A small "?" trigger that opens a Popover explaining what the ecosystem
 * network visualization shows and how a first-time visitor can use it. The
 * graph itself has no legend text for a newcomer: a dot is an organization
 * and a line is a connection, but nothing on the canvas says that, so
 * someone arriving cold (the ticket's example: "someone from Garfield School
 * District") has no way to know what they are looking at or why it matters
 * to them.
 *
 * Deliberately a small on-demand disclosure rather than always-visible text
 * or a blocking Modal (like OnboardingTour): the ticket's own QA notes ask
 * that the explanation "not clutter the page or distract from the
 * visualization," so it stays out of the way until a visitor asks for it.
 * Built on `OverlayTrigger` (trigger="click", rootClose) rather than a hand
 * rolled Overlay + useState: rootClose gives Escape-key and outside-click
 * dismissal for free, which a raw Overlay does not — and the useState
 * version's own toggle click was indistinguishable from rootClose's
 * "outside click," so it deliberately shipped without any dismissal but a
 * second click on the trigger. OverlayTrigger avoids that ambiguity because
 * it owns the show/hide wiring itself, so the ambiguous click is not the
 * caller's problem to solve (round-1 review, findings 2 and 4).
 *
 * Generic and reusable (ticket's recommended option (b), not COSTEM-authored
 * content): rendered by EcosystemGraph (the Directory's full graph),
 * EcosystemGraphPreview (the graph-preview content block and the platform
 * hero), and GraphMobileView (the list-based mobile fallback, via
 * `context="list"` — see COPY below), so every tenant gets the explainer
 * automatically rather than each one having to author it into their landing
 * content. All three call sites already sit behind the `networkGraph`
 * capability gate applied in graph/index.js's barrel export, so this
 * component does not need its own capability check — it only ever renders
 * on a surface a tenant has already enabled the graph for.
 */
import React, { useId, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import PropTypes from 'prop-types';

// `context="map"` (EcosystemGraph, EcosystemGraphPreview) describes the
// canvas visualization; `context="list"` (GraphMobileView) describes the
// same underlying data as it actually appears on that surface — rows in a
// list rather than dots on a canvas. `ariaLabel` keeps the trigger's
// accessible name in sync with the popover's own visible title, rather than
// announcing "network visualization" over a surface that's actually a list.
const COPY = {
    map: {
        title: 'How to read this map',
        ariaLabel: 'How to read this network visualization',
        body: (
            <p className="mb-2">
                Each dot is an organization in the ecosystem; a line between two
                dots means they have an established connection, such as a
                partnership or shared program.
            </p>
        ),
    },
    list: {
        title: 'How to read this list',
        ariaLabel: 'How to read this list of organizations',
        body: (
            <p className="mb-2">
                Each row is an organization in the ecosystem, with its number of
                established connections — such as partnerships or shared
                programs — shown alongside it.
            </p>
        ),
    },
};

export default function GraphExplainer({
    variant = 'light',
    className = '',
    context = 'map',
}) {
    const [show, setShow] = useState(false);
    // A page can mount this component more than once (e.g. a tenant landing
    // page composed of both a hero graph preview and a graph-preview content
    // block): a hardcoded popover id would collide between instances, and
    // aria-controls needs a real id to point at in the first place.
    const popoverId = useId();
    const copy = COPY[context];

    const popover = (
        <Popover id={popoverId} className="graph-explainer-popover">
            {/* as="div", not the default h3: this popover can appear
                anywhere on a page (including inside the Directory's own
                heading hierarchy), and there is no guarantee an h3 here
                would land at the right level. */}
            <Popover.Header as="div" className="fw-semibold">
                {copy.title}
            </Popover.Header>
            <Popover.Body>
                {copy.body}
                <p className="mb-0">
                    Try it: find an organization in your region, then follow its
                    connections to see who else it already works with in your
                    focus area.
                </p>
            </Popover.Body>
        </Popover>
    );

    return (
        <OverlayTrigger
            trigger="click"
            placement="bottom-end"
            rootClose
            transition={false}
            show={show}
            onToggle={setShow}
            overlay={popover}
        >
            <button
                type="button"
                className={`graph-explainer-trigger btn btn-sm btn-${variant} rounded-circle ${className}`}
                aria-label={copy.ariaLabel}
                aria-expanded={show}
                // OverlayTrigger only mounts the popover while open, so
                // aria-controls would otherwise be a dangling IDREF in the
                // closed state.
                aria-controls={show ? popoverId : undefined}
            >
                ?
            </button>
        </OverlayTrigger>
    );
}

GraphExplainer.propTypes = {
    // Bootstrap button variant for the trigger. Overlays on a light legend
    // background want 'light'; a dark hero background wants 'outline-light'.
    variant: PropTypes.string,
    className: PropTypes.string,
    // 'map' (default) for the canvas visualizations; 'list' for
    // GraphMobileView's row-based fallback.
    context: PropTypes.oneOf(['map', 'list']),
};
